import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { StorageService, STORAGE_KEYS } from './storage.service';
import {
  LoginCredentials,
  LoginResponse,
  MfaVerifyRequest,
  MfaResendRequest,
} from '../models/auth.models';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private storage = inject(StorageService);

  private _isAuthenticated = signal(false);
  private _username = signal('');
  private _tenantId = signal('');

  isAuthenticated = this._isAuthenticated.asReadonly();
  username = this._username.asReadonly();
  tenantId = this._tenantId.asReadonly();

  constructor() {
    const token = this.storage.getItem<string>(STORAGE_KEYS.LOGIN_TOKEN);
    const user = this.storage.getItem<Record<string, unknown>>(STORAGE_KEYS.GLOBAL_USER);
    const tid = this.storage.getItem<string>(STORAGE_KEYS.TENANT_ID);
    if (token) {
      this._isAuthenticated.set(true);
      this._tenantId.set(tid ?? '');
      this._username.set(this.displayNameFromUser(user));
    }
  }

  private apiUrl(path: string): string {
    const base = environment.apiBaseUrl.replace(/\/$/, '');
    const p = path.startsWith('/') ? path : `/${path}`;
    return base ? `${base}${p}` : p;
  }

  private jsonHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/json' });
  }

  login(credentials: LoginCredentials): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(this.apiUrl(environment.loginUrl), credentials, {
      headers: this.jsonHeaders(),
    });
  }

  verifyMfa(req: MfaVerifyRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(this.apiUrl(environment.mfaVerifyUrl), req, {
      headers: this.jsonHeaders(),
    });
  }

  resendMfa(req: MfaResendRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(this.apiUrl(environment.mfaResendUrl), req, {
      headers: this.jsonHeaders(),
    });
  }

  logout(): void {
    const token = this.getLoginToken();
    const tid = this.getTenantId();
    const finish = (): void => {
      this.storage.clear();
      this._isAuthenticated.set(false);
      this._username.set('');
      this._tenantId.set('');
      this.router.navigate(['/login']);
    };

    if (token) {
      let headers = this.jsonHeaders().set('Authorization', `Bearer ${token}`);
      if (tid) {
        headers = headers.set('x-tenant-id', tid);
      }
      this.http.post(this.apiUrl(environment.logoutUrl), {}, { headers }).subscribe({
        next: () => finish(),
        error: () => finish(),
      });
    } else {
      finish();
    }
  }

  private displayNameFromUser(user: Record<string, unknown> | null): string {
    if (!user) return '';
    const name = user['name'];
    if (typeof name === 'string' && name.trim()) return name;
    const first = user['firstName'];
    const last = user['lastName'];
    const parts = [first, last].filter(p => typeof p === 'string' && (p as string).trim());
    if (parts.length) return parts.join(' ');
    const email = user['email'];
    return typeof email === 'string' ? email : '';
  }

  handleSuccessfulLogin(data: NonNullable<LoginResponse['data']>): void {
    const tenantId = data.shards?.[0]?.tenantId;
    if (!tenantId) {
      console.error('AuthService: Unable to determine tenant');
      return;
    }

    const existingUser = this.storage.getItem<Record<string, unknown>>(STORAGE_KEYS.GLOBAL_USER);
    let userToStore: Record<string, unknown> = data.globalUser ?? {};
    if (!userToStore['profilePictureUrl'] && existingUser?.['profilePictureUrl']) {
      userToStore = { ...userToStore, profilePictureUrl: existingUser['profilePictureUrl'] };
    }

    this.storage.setItem(STORAGE_KEYS.LOGIN_TOKEN, data.loginToken!);
    this.storage.setItem(STORAGE_KEYS.GLOBAL_USER, userToStore);
    this.storage.setItem(STORAGE_KEYS.TENANT_ID, tenantId);
    this.storage.removeItem(STORAGE_KEYS.MFA_TOKEN);
    this.storage.removeItem(STORAGE_KEYS.AUTH_DATA);

    this._isAuthenticated.set(true);
    this._tenantId.set(tenantId);
    this._username.set(this.displayNameFromUser(userToStore));

    this.router.navigate([`/${tenantId}/wip`]);
  }

  /**
   * Deep-link handoff from AIO (or any launcher): URL carries tenant + encoded JWT; no API calls.
   */
  applyHandoffFromUrl(tenantId: string, token: string): void {
    const tid = tenantId.trim();
    const tok = token.trim();
    if (!tid || !tok) return;

    const claims = this.tryDecodeJwtPayload(tok);
    const userFromClaims = this.globalUserFromJwtClaims(claims);

    this.storage.setItem(STORAGE_KEYS.LOGIN_TOKEN, tok);
    this.storage.setItem(STORAGE_KEYS.TENANT_ID, tid);
    this.storage.setItem(STORAGE_KEYS.GLOBAL_USER, userFromClaims);
    this.storage.removeItem(STORAGE_KEYS.MFA_TOKEN);
    this.storage.removeItem(STORAGE_KEYS.AUTH_DATA);

    this._isAuthenticated.set(true);
    this._tenantId.set(tid);
    this._username.set(this.displayNameFromUser(userFromClaims));
  }

  private tryDecodeJwtPayload(jwt: string): Record<string, unknown> | null {
    try {
      const parts = jwt.split('.');
      if (parts.length < 2) return null;
      let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const pad = base64.length % 4;
      if (pad) base64 += '='.repeat(4 - pad);
      const json = atob(base64);
      return JSON.parse(json) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  private globalUserFromJwtClaims(claims: Record<string, unknown> | null): Record<string, unknown> {
    if (!claims) return {};
    const out: Record<string, unknown> = {};
    const name = claims['name'];
    if (typeof name === 'string' && name.trim()) out['name'] = name.trim();
    const email = claims['email'];
    if (typeof email === 'string' && email.trim()) out['email'] = email.trim();
    const uniqueName = claims['unique_name'];
    if (!out['email'] && typeof uniqueName === 'string' && uniqueName.includes('@')) {
      out['email'] = uniqueName.trim();
    }
    const given = claims['given_name'];
    const family = claims['family_name'];
    if (typeof given === 'string' && given.trim()) out['firstName'] = given.trim();
    if (typeof family === 'string' && family.trim()) out['lastName'] = family.trim();
    return out;
  }

  getLoginToken(): string | null {
    return this.storage.getItem<string>(STORAGE_KEYS.LOGIN_TOKEN);
  }

  getTenantId(): string | null {
    return this.storage.getItem<string>(STORAGE_KEYS.TENANT_ID);
  }

  getGlobalUser(): Record<string, unknown> | null {
    return this.storage.getItem<Record<string, unknown>>(STORAGE_KEYS.GLOBAL_USER);
  }

  getUserInitial(): string {
    const u = this._username();
    return u ? u.charAt(0).toUpperCase() : 'U';
  }
}
